import h from 'hyperscript';

import analysisIcon from '@disy/cadenza-icons/analysis-layer.svg';
import polygonIcon from '@disy/cadenza-icons/area.svg';
import circleSectorIcon from '@disy/cadenza-icons/circle-sector.svg';
import clusterIcon from '@disy/cadenza-icons/cluster.svg';
import dataAcquisitionIcon from '@disy/cadenza-icons/data-acquisition-layer.svg';
import dataViewIcon from '@disy/cadenza-icons/data-view-layer.svg';
import pointIcon from '@disy/cadenza-icons/dots.svg';
import externalIcon from '@disy/cadenza-icons/external-layer.svg';
import gridIcon from '@disy/cadenza-icons/grid-squares-layer.svg';
import heatmapIcon from '@disy/cadenza-icons/heatmap.svg';
import isochronesIcon from '@disy/cadenza-icons/isochrones-theme.svg';
import groupIcon from '@disy/cadenza-icons/layer-group.svg';
import lineIcon from '@disy/cadenza-icons/lines.svg';
import pieIcon from '@disy/cadenza-icons/pie.svg';
import rasterIcon from '@disy/cadenza-icons/raster.svg';
import rasterLayerIcon from '@disy/cadenza-icons/rasterlayer.svg';
import routeIcon from '@disy/cadenza-icons/routing-theme.svg';
import sketchIcon from '@disy/cadenza-icons/sketch-layer.svg';
import unknownIcon from '@disy/cadenza-icons/unknown-layer.svg';
import { addStyleClass } from '@disy/lib/add-style-class';
import { assertType } from '@disy/lib/custom-error';
import type { Content } from '@disy/ui/content';
import type { Icon, IconOptions } from '@disy/ui/icon';
import { icon } from '@disy/ui/icon';
import { LabelWithIcon } from '@disy/ui/label-with-icon';

import { isMap } from 'cadenza/workbook/api/data-view-visualization-key';
import type { MapVisualizationKey } from 'cadenza/workbook/api/data-view-visualization-key';

import styles from './layer-label.module.css' with { type: 'css' };
import i18n from './layer-label.properties';

export const LAYER_LABEL_TYPE_VALUES = [
  'analysis',
  'circle-sector',
  'cluster',
  'data-acquisition',
  'data-view',
  'external',
  'grid',
  'group',
  'heatmap',
  'isochrones',
  'line',
  'pie',
  'point',
  'polygon',
  'raster',
  'raster-layer',
  'route',
  'sketch',
  'unknown',
] as const;

export type LayerLabelType = (typeof LAYER_LABEL_TYPE_VALUES)[number];

const ICON_BY_LAYER_TYPE: Record<LayerLabelType, string> = {
  analysis: analysisIcon,
  'circle-sector': circleSectorIcon,
  cluster: clusterIcon,
  'data-acquisition': dataAcquisitionIcon,
  'data-view': dataViewIcon,
  external: externalIcon,
  grid: gridIcon,
  group: groupIcon,
  heatmap: heatmapIcon,
  isochrones: isochronesIcon,
  line: lineIcon,
  pie: pieIcon,
  polygon: polygonIcon,
  point: pointIcon,
  raster: rasterIcon,
  'raster-layer': rasterLayerIcon,
  route: routeIcon,
  sketch: sketchIcon,
  unknown: unknownIcon,
};

type NonDataViewLayerType = 'MAP.EXTERNAL' | 'MAP.GROUP' | 'MAP.SKETCH';

const MAP_VISUALIZATION_TO_LAYER_TYPE: Record<MapVisualizationKey | NonDataViewLayerType, LayerLabelType> = {
  'MAP.LINE': 'line',
  'MAP.ROUTES': 'route',
  'MAP.ISOCHRONES': 'isochrones',
  'MAP.HEATMAP': 'heatmap',
  'MAP.POLYGON': 'polygon',
  'MAP.MAP_PIE_CHART': 'pie',
  'MAP.POINT': 'point',
  'MAP.CLUSTERING_POINT': 'cluster',
  'MAP.EXTERNAL': 'external',
  'MAP.POSTGIS_RASTER': 'raster',
  'MAP.GROUP': 'group',
  'MAP.SKETCH': 'sketch',
  'MAP.CIRCLE_SECTORS': 'circle-sector',
};

function visualizationToLayerType(visualizationKey: MapVisualizationKey | NonDataViewLayerType): LayerLabelType {
  return MAP_VISUALIZATION_TO_LAYER_TYPE[visualizationKey] ?? 'data-view';
}

function isMapViewType(
  type: LayerLabelType | MapVisualizationKey | NonDataViewLayerType,
): type is MapVisualizationKey | NonDataViewLayerType {
  return isMap(type) || !!MAP_VISUALIZATION_TO_LAYER_TYPE[type as MapVisualizationKey | NonDataViewLayerType];
}

export function layerIcon(
  type: LayerLabelType | MapVisualizationKey | NonDataViewLayerType,
  options?: IconOptions,
): Icon {
  type = isMapViewType(type) ? visualizationToLayerType(type) : type;
  // Add fallback icon for map layers created from template layers.
  type = LAYER_LABEL_TYPE_VALUES.includes(type) ? type : 'data-view';
  return icon(ICON_BY_LAYER_TYPE[type], { label: i18n(type), ...options });
}

const COMPONENT_NAME = 'd-layer-label';

class LayerLabel extends LabelWithIcon {
  constructor() {
    super(() => layerIcon(this.type));
    this.shadowRoot.adoptedStyleSheets = [...this.shadowRoot.adoptedStyleSheets, styles];
  }

  override connectedCallback() {
    this.largerIcon = true;
    super.connectedCallback();
  }

  set type(type: LayerLabelType | MapVisualizationKey) {
    this.setAttribute('type', type);
  }

  get type() {
    const type = this.getAttribute('type');
    assertType<LayerLabelType | MapVisualizationKey>(
      type,
      LAYER_LABEL_TYPE_VALUES.includes(type as LayerLabelType) ||
        isMapViewType(type as MapVisualizationKey | NonDataViewLayerType),
    );
    return type;
  }
}

customElements.define(COMPONENT_NAME, LayerLabel);
declare global {
  interface HTMLElementTagNameMap {
    [COMPONENT_NAME]: LayerLabel;
  }
}

export function createLayerLabel(
  type: LayerLabelType | MapVisualizationKey,
  name: Content,
  { styleClass }: { styleClass?: string | string[] } = {},
) {
  const label = h<LayerLabel>(COMPONENT_NAME, { type }, name);
  addStyleClass(label, styleClass);
  return label;
}
